import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { MdCheckCircle } from 'react-icons/md'
import logger from '@/utils/logger'
import authService from '@/services/authService'
import ValidationSummary from '@/components/common/ValidationSummary'
import { classifyIdentifier } from '@/utils/identifier'
import type { ServerError, ValidationErrors } from '@/types'

/**
 * Forgot-password step. Rendered inside the login-page shell so it shares the
 * same two-column popup, brand panel and footer as the primary login flow.
 *
 * One field, matching the sign-in step (FEDEV-528): the customer types their
 * email, mobile number or customer number and we work out which it is. The
 * reset link is emailed as before, and now also texted when the customer
 * identified themselves by mobile.
 *
 * The backend always answers the same regardless of whether the details
 * matched a real account (anti-enumeration), so the success screen is
 * deliberately vague and channel-neutral.
 */

function ForgotPasswordForm(): React.JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Pre-fill from the login flow when the customer arrived via "Forgot your
  // password?" on the password step. Either param is honoured; harmless if
  // both are absent.
  const [identifier, setIdentifier] = useState(
    searchParams.get('identifier') ?? searchParams.get('email') ?? '',
  )
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [apiError, setApiError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({})

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setIdentifier(e.target.value)
    if (errors.identifier) {
      setErrors((prev) => ({ ...prev, identifier: '' }))
    }
  }

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault()
    setApiError('')
    setErrors({})
    setValidationErrors({})

    if (identifier.trim() === '') {
      setErrors({
        identifier: 'Enter your email address, mobile number or customer number',
      })
      return
    }

    setIsLoading(true)
    try {
      const { email, mobile, customerNumber } = classifyIdentifier(identifier)
      await authService.forgotPassword({
        email: email || undefined,
        mobile: mobile || undefined,
        customer_number: customerNumber || undefined,
      })
      setSuccess(true)
      setIdentifier('')
    } catch (error: unknown) {
      const resetPasswordError = error as ServerError
      logger.error('Forgot password error:', resetPasswordError)

      setApiError(
        (resetPasswordError?.body?.message as string) ||
          resetPasswordError?.message ||
          'Something went wrong. Please try again.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="login-step login-step--neutral">
        <div className="login-step-icon login-step-icon--success">
          <MdCheckCircle size={48} />
        </div>
        <h1 className="login-step-heading">Check your messages</h1>
        <p className="login-step-subheading">
          If those details match an account, a password reset link is on its
          way. The link expires in 60 minutes.
        </p>

        <div className="login-info-card muted">
          <p>
            <strong>Didn't get it?</strong> Check your spam folder, or your
            texts if you gave us your mobile number.
          </p>
        </div>

        <Link to="/login" className="btn btn-primary btn-lg btn-block">
          Back to sign in
        </Link>
      </div>
    )
  }

  return (
    <div className="login-step">
      <h1 className="login-step-heading">Reset your password</h1>
      <p className="login-step-subheading">
        We'll send you a link to set a new password.
      </p>

      <ValidationSummary errors={validationErrors} message={apiError} />

      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <div className="form-group">
          <label htmlFor="identifier" className="form-label">
            Email address, mobile number or customer number
          </label>
          <input
            type="text"
            id="identifier"
            value={identifier}
            onChange={handleChange}
            className={`form-control form-control-lg ${errors.identifier ? 'is-invalid' : ''}`}
            autoComplete="username"
            autoFocus
            disabled={isLoading}
          />
          {errors.identifier && (
            <span className="form-text is-invalid">{errors.identifier}</span>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-lg btn-block"
          disabled={isLoading}
        >
          {isLoading ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      <div className="login-step-footer">
        <button
          type="button"
          className="btn btn-link"
          onClick={() => navigate('/login')}
        >
          Back
        </button>
      </div>
    </div>
  )
}

export default ForgotPasswordForm
